import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { STATUS_TONE, healthTone } from "@/pages/Standorte";
import { Heartbeat, Plus, PuzzlePiece, Barcode, CaretLeft, PencilSimple, WarningCircle, ClockCountdown, ClipboardText, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const COMP_TONE = { ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", wartung_faellig: "border-amber-500/40 bg-amber-500/10 text-amber-600", pruefung_unvollstaendig: "border-orange-500/40 bg-orange-500/10 text-orange-600", mangel: "border-amber-500/40 bg-amber-500/10 text-amber-600", sicherheitsmangel: "border-red-500/40 bg-red-500/10 text-red-600", ausser_betrieb: "border-red-500/40 bg-red-500/10 text-red-600" };
const RULE_TONE = { ok: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", faellig: "border-amber-500/40 bg-amber-500/10 text-amber-600", ueberfaellig: "border-red-500/40 bg-red-500/10 text-red-600", unbekannt: "border-border bg-muted text-muted-foreground" };
const RULE_LABEL = { ok: "OK", faellig: "Fällig", ueberfaellig: "Überfällig", unbekannt: "Kein Datum" };
const DEFECT_TONE = { offen: "border-amber-500/40 bg-amber-500/10 text-amber-600", beauftragt: "border-blue-500/40 bg-blue-500/10 text-blue-600", behoben: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", abgelehnt: "border-red-500/40 bg-red-500/10 text-red-600" };
const KLASSE_TONE = { geringfuegig: "text-emerald-600", erheblich: "text-amber-600", gefaehrlich: "text-red-600" };
const groupTypes = (types) => types.reduce((m, t) => { const k = t.kategorie || "Sonstiges"; (m[k] = m[k] || []).push(t); return m; }, {});
const emptyForm = { kind: "", bezeichnung: "", manufacturer: "", type: "", serial_no: "", baujahr: "" };

export default function AnlageAkte() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [parent, setParent] = useState(null);
  const [comps, setComps] = useState(null);
  const [status, setStatus] = useState(null);
  const [compTypes, setCompTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rules, setRules] = useState(null);
  const [history, setHistory] = useState(null);
  const [defects, setDefects] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [health, setHealth] = useState(null);
  const [logInput, setLogInput] = useState({});

  const load = useCallback(() => {
    api.get(`/assets/${id}`).then((r) => {
      const a = r.data.asset || r.data;
      setAsset(a);
      if (a?.parent_id) api.get(`/assets/${a.parent_id}`).then((pr) => setParent(pr.data.asset || pr.data)).catch(() => {});
    }).catch(() => setAsset({ id, name: "Anlage" }));
    api.get(`/v1/assets/${id}/components`).then((r) => setComps(r.data));
    api.get(`/v1/assets/${id}/status`).then((r) => setStatus(r.data));
    api.get("/v1/catalogs/component-types").then((r) => setCompTypes(r.data));
    api.get(`/v1/assets/${id}/service-rules`).then((r) => setRules(r.data)).catch(() => setRules({ rules: [], warnings: [] }));
    api.get(`/v1/assets/${id}/maintenance-history`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
    api.get(`/v1/assets/${id}/defects`).then((r) => setDefects(r.data)).catch(() => setDefects([]));
    api.get(`/v1/assets/${id}/twin-knowledge`).then((r) => setKnowledge(r.data)).catch(() => setKnowledge(null));
    api.get(`/v1/assets/${id}/health`).then((r) => setHealth(r.data)).catch(() => setHealth(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveLog = async (ruleKey) => {
    const val = logInput[ruleKey];
    if (!val) return;
    try { await api.put(`/v1/assets/${id}/service-log`, { rule_key: ruleKey, last_done: val }); toast.success("Durchführung erfasst"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCreate = () => { setEditId(null); setForm({ ...emptyForm, kind: compTypes[0]?.key || "sonstiges" }); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ kind: c.kind, bezeichnung: c.bezeichnung || "", manufacturer: c.manufacturer || "", type: c.type || "", serial_no: c.serial_no || "", baujahr: c.baujahr || "" }); setOpen(true); };

  const save = async () => {
    if (!form.bezeichnung || !form.kind) return;
    try {
      if (editId) { await api.put(`/v1/components/${editId}`, form); toast.success("Komponente aktualisiert"); }
      else { await api.post(`/v1/assets/${id}/components`, form); toast.success("Komponente hinzugefügt"); }
      setOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!comps || !status) return <div className="space-y-4"><Skeleton className="h-24 rounded-md" /><Skeleton className="h-48 rounded-md" /></div>;
  const typeGroups = groupTypes(compTypes);
  const typeName = (key) => compTypes.find((t) => t.key === key)?.name || key;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><CaretLeft size={16} /> Zurück</button>
      <PageHeader title={asset?.name || "Anlagenakte"} subtitle={`${asset?.asset_type || "Anlage"} · QR ${asset?.qr_code || "—"}`}>
        {asset?.kundenbezeichnung && <Badge variant="outline" className="rounded-md" data-testid="anlage-kundenbez">Kunde: {asset.kundenbezeichnung}</Badge>}
        <Badge variant="outline" className={`rounded-md ${STATUS_TONE[status.status] || ""}`} data-testid="anlage-status">{status.status}</Badge>
        <Badge variant="outline" className={`gap-1 rounded-md ${healthTone(status.health_index)}`} data-testid="anlage-health"><Heartbeat size={13} weight="fill" />v1 {status.health_index}%</Badge>
        {health?.v2 && <Badge variant="outline" className={`gap-1 rounded-md ${healthTone(health.v2.health_index)}`} data-testid="anlage-health-v2"><Heartbeat size={13} weight="fill" />v2 {health.v2.health_index}%</Badge>}
      </PageHeader>

      {asset?.operational_status === "ausgefallen" && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-600" data-testid="anlage-blocked-banner">
          <WarningCircle size={18} weight="fill" /> <b>Anlage gesperrt (ausgefallen)</b> — automatisch durch Business Rule wegen gefährlichem sicherheitsrelevantem Mangel.
        </div>
      )}

      {parent && (
        <button onClick={() => navigate(`/anlage/${parent.id}`)} data-testid="parent-link"
          className="mb-4 inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent">
          Teil von: <span className="font-semibold text-foreground">{parent.name}</span>
        </button>
      )}

      <Card className="rounded-md border" data-testid="component-list">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-2"><PuzzlePiece size={18} weight="duotone" className="text-primary" /><Label2>Komponenten ({status.components_total})</Label2></div>
          <Button size="sm" variant="outline" className="gap-2" data-testid="add-component-btn" onClick={openCreate}><Plus size={15} weight="bold" /> Komponente</Button>
        </div>
        <div className="divide-y divide-border">
          {comps.length === 0 && <div className="p-6 text-sm text-muted-foreground">Noch keine Komponenten.</div>}
          {comps.map((c) => (
            <div key={c.id} data-testid={`comp-${c.id}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold">{c.bezeichnung}</span>
                  {c.is_hauptkomponente && <Badge variant="outline" className="rounded-md text-[10px]">Haupt</Badge>}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="uppercase tracking-wider">{typeName(c.kind)}</span>
                  {c.manufacturer && <span>· {c.manufacturer} {c.type || ""}</span>}
                  {c.serial_no && <span className="flex items-center gap-0.5"><Barcode size={11} />{c.serial_no}</span>}
                  {c.baujahr && <span>· BJ {c.baujahr}</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="outline" className={`rounded-md text-xs ${COMP_TONE[c.status] || ""}`}>{c.status}</Badge>
                <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`comp-edit-${c.id}`} onClick={() => openEdit(c)}><PencilSimple size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {rules && rules.rules.length > 0 && (
        <Card className="mt-6 rounded-md border" data-testid="service-rules-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <ClockCountdown size={18} weight="duotone" className="text-primary" />
            <Label2>Fälligkeiten & Wartungsintervalle</Label2>
            {rules.warnings.length > 0 && <Badge variant="outline" className="rounded-md border-red-500/40 bg-red-500/10 text-red-600" data-testid="rules-warning-count">{rules.warnings.length} fällig/überfällig</Badge>}
          </div>
          <div className="divide-y divide-border">
            {rules.rules.map((r) => (
              <div key={r.key} data-testid={`rule-${r.key}`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {(r.status === "ueberfaellig" || r.status === "faellig") && <WarningCircle size={15} weight="fill" className="text-red-500" />}
                    {r.label}
                    <Badge variant="outline" className={`rounded-md text-[10px] ${RULE_TONE[r.status] || ""}`}>{RULE_LABEL[r.status] || r.status}</Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Intervall {Math.round(r.interval_months / 12 * 10) / 10} J{r.norm ? ` · ${r.norm}` : ""}
                    {r.last_done ? ` · zuletzt ${r.last_done}` : " · noch nicht erfasst"}
                    {r.due_date ? ` · fällig ${r.due_date}` : ""}
                  </div>
                  {r.hinweis && <div className="mt-0.5 text-xs italic text-muted-foreground">{r.hinweis}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Input type="date" className="h-8 w-40 text-sm" data-testid={`rule-date-${r.key}`} value={logInput[r.key] || ""} onChange={(e) => setLogInput({ ...logInput, [r.key]: e.target.value })} />
                  <Button size="sm" variant="outline" className="h-8" data-testid={`rule-save-${r.key}`} onClick={() => saveLog(r.key)}>Erfassen</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {history && history.length > 0 && (
        <Card className="mt-6 rounded-md border" data-testid="maintenance-history-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <ClipboardText size={18} weight="duotone" className="text-primary" />
            <Label2>Wartungshistorie ({history.length})</Label2>
          </div>
          <div className="divide-y divide-border">
            {history.map((h, i) => (
              <button key={i} onClick={() => navigate(`/wartungsauftraege/${h.job_id}`)} data-testid={`history-${i}`}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium"><CheckCircle size={15} weight="fill" className="text-emerald-600" />{h.completed_at ? new Date(h.completed_at).toLocaleString("de-DE") : "—"}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{h.completed_by || "—"} · {h.mangel_count + h.sicherheitsmangel_count} Mangel/Mängel · {h.unpruef_count} ungeprüft</div>
                </div>
                {h.health_before != null && <Badge variant="outline" className="rounded-md text-xs">Health {h.health_before}% → {h.health_after}%</Badge>}
              </button>
            ))}
          </div>
        </Card>
      )}

      {defects && defects.length > 0 && (
        <Card className="mt-6 rounded-md border" data-testid="anlage-defects-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <WarningCircle size={18} weight="duotone" className="text-primary" />
            <Label2>Offene Mängel & Maßnahmen ({defects.filter((d) => d.status !== "behoben" && d.status !== "abgelehnt").length})</Label2>
          </div>
          <div className="divide-y divide-border">
            {defects.map((d) => (
              <div key={d.id} data-testid={`anlage-defect-${d.id}`} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    {d.sicherheitsrelevant && <WarningCircle size={15} weight="fill" className="text-red-500" />}
                    {d.bezeichnung}
                    {d.mangelklasse && <span className={`text-[10px] uppercase ${KLASSE_TONE[d.mangelklasse] || ""}`}>{d.mangelklasse}</span>}
                    {d.prioritaet && <span className="text-[10px] uppercase text-muted-foreground">· {d.prioritaet}</span>}
                  </div>
                  {d.massnahme && <div className="mt-0.5 text-xs text-muted-foreground">Maßnahme: {d.massnahme}</div>}
                </div>
                <Badge variant="outline" className={`rounded-md text-xs ${DEFECT_TONE[d.status] || ""}`}>{d.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {knowledge && knowledge.components.some((c) => c.core) && (
        <Card className="mt-6 rounded-md border" data-testid="anlage-knowledge-card">
          <div className="flex items-center gap-2 border-b border-border p-4">
            <PuzzlePiece size={18} weight="duotone" className="text-primary" />
            <Label2>CMS-Wissen (aus CMS-Core)</Label2>
            <Badge variant="outline" className="rounded-md text-[10px]">{knowledge.offene_zuordnungen} offene Zuordnung(en)</Badge>
          </div>
          <div className="divide-y divide-border">
            {knowledge.components.filter((c) => c.core).map((c) => (
              <div key={c.component_id} data-testid={`anlage-know-${c.component_id}`} className="px-4 py-3">
                <div className="text-sm font-semibold">{c.bezeichnung} <span className="font-mono text-[10px] text-muted-foreground">{c.cms_komponente_key}</span></div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {c.core.maengel.map((m) => <Badge key={m.key} variant="outline" className="rounded-md text-[11px]">{m.name}</Badge>)}
                  {c.core.reparatur_pakete.map((r) => <Badge key={r.key} variant="outline" className="rounded-md border-blue-500/30 text-[11px]">{r.name}</Badge>)}
                  {c.core.normen.map((nm) => <Badge key={nm.key} variant="outline" className="rounded-md text-[11px] text-muted-foreground">{nm.name}</Badge>)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="component-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Komponente bearbeiten" : "Komponente hinzufügen"}</DialogTitle></DialogHeader>
          <DialogDescription>Komponente dauerhaft dieser Anlage zuordnen.</DialogDescription>
          <div className="space-y-3">
            <div><Label2>Typ</Label2>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="comp-kind"><SelectValue placeholder="Typ wählen" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(typeGroups).map(([kat, list]) => (
                    <SelectGroup key={kat}><SelectLabel>{kat}</SelectLabel>
                      {list.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input data-testid="comp-bezeichnung" placeholder="Bezeichnung" value={form.bezeichnung} onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Hersteller" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} />
              <Input placeholder="Typ" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
              <Input placeholder="Seriennummer" value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} />
              <Input placeholder="Baujahr" value={form.baujahr} onChange={(e) => setForm({ ...form, baujahr: e.target.value })} />
            </div>
          </div>
          <DialogFooter><Button data-testid="comp-save" onClick={save} disabled={!form.bezeichnung || !form.kind}>{editId ? "Speichern" : "Hinzufügen"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
